import { useState, useEffect } from "react";
import { 
  BarChart3, TrendingUp, Users, Building2, Crown, Star, 
  Heart, FileText, PoundSterling, MapPin, Eye, UserCheck,
  Calendar, ArrowUp, ArrowDown
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple bar chart component
function SimpleBarChart({ data, labelKey, valueKey, maxValue }) {
  const max = maxValue || Math.max(...data.map(d => d[valueKey]));
  
  return (
    <div className="space-y-2">
      {data.map((item, i) => (
        <div key={item[labelKey] || item.id || `bar-${i}`} className="flex items-center gap-3">
          <span className="text-xs text-gray-600 w-20 truncate">{item[labelKey]}</span>
          <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(item[valueKey] / max) * 100}%` }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="h-full bg-gradient-to-r from-brand to-brand-dark rounded-full"
            />
          </div>
          <span className="text-sm font-medium text-gray-900 w-10 text-right">{item[valueKey]}</span>
        </div>
      ))}
    </div>
  );
}

// Line chart component for time series
function SimpleLineChart({ data, dataKey, color = "#2563eb", height = 120 }) {
  if (!data || data.length === 0) return null;
  
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const min = 0;
  const range = max - min || 1;
  
  // Generate SVG path
  const width = 100;
  const chartHeight = height - 20;
  const points = values.map((val, i) => {
    const x = (i / (values.length - 1 || 1)) * width;
    const y = chartHeight - ((val - min) / range) * chartHeight;
    return `${x},${y}`;
  });
  
  const linePath = `M ${points.join(' L ')}`;
  const areaPath = `${linePath} L ${width},${chartHeight} L 0,${chartHeight} Z`;
  
  return (
    <div className="relative" style={{ height }}>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="none">
        {/* Area fill */}
        <path
          d={areaPath}
          fill={`${color}15`}
          strokeWidth="0"
        />
        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth="0.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Points */}
        {values.map((val, i) => {
          const x = (i / (values.length - 1 || 1)) * width;
          const y = chartHeight - ((val - min) / range) * chartHeight;
          return (
            <circle
              key={`point-${i}-${val}`}
              cx={x}
              cy={y}
              r="1"
              fill={color}
            />
          );
        })}
      </svg>
      {/* Y-axis labels */}
      <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[10px] text-gray-400 -ml-6">
        <span>{max}</span>
        <span>{Math.round(max / 2)}</span>
        <span>0</span>
      </div>
    </div>
  );
}

// Stat Card
function StatCard({ icon: Icon, label, value, subtext, color = "blue", trend }) {
  const colors = {
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    amber: "from-amber-500 to-amber-600",
    purple: "from-purple-500 to-purple-600",
    red: "from-red-500 to-red-600",
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-5 shadow-sm border border-gray-100"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
          {trend !== undefined && trend !== 0 && (
            <div className={`flex items-center gap-1 mt-1 text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
              <span>{Math.abs(trend)}% vs last period</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-gradient-to-br ${colors[color]}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </motion.div>
  );
}

// Admin Analytics Dashboard
export function AdminAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [historical, setHistorical] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  useEffect(() => {
    fetchHistorical();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistorical = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/historical?days=${period}`);
      setHistorical(response.data);
    } catch (error) {
      console.error("Failed to fetch historical:", error);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-28 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return <div className="text-center text-gray-500 py-8">Failed to load analytics</div>;
  }

  const venueTypeData = Object.entries(analytics.venue_types || {})
    .map(([type, count]) => ({ type: type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Unknown', count }))
    .filter(d => d.count > 0)
    .sort((a, b) => b.count - a.count);

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          icon={Building2} 
          label="Total Venues" 
          value={analytics.total_venues} 
          color="blue"
        />
        <StatCard 
          icon={Users} 
          label="Venue Owners" 
          value={analytics.total_owners} 
          color="green"
        />
        <StatCard 
          icon={Crown} 
          label="Premium Subscribers" 
          value={analytics.total_premium}
          subtext={`£${analytics.revenue?.estimated_annual?.toFixed(0) || 0}/year`}
          color="amber"
        />
        <StatCard 
          icon={Star} 
          label="Total Reviews" 
          value={analytics.total_reviews} 
          color="purple"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          icon={Heart} 
          label="Total Favorites" 
          value={analytics.total_favorites} 
          color="red"
        />
        <StatCard 
          icon={UserCheck} 
          label="Visitors" 
          value={analytics.visitors || 0}
          subtext={`${analytics.visitors_today || 0} today`}
          color="blue"
        />
        <StatCard 
          icon={FileText} 
          label="Pending Reviews" 
          value={analytics.pending_submissions} 
          color="amber"
        />
        <StatCard 
          icon={FileText} 
          label="Approved" 
          value={analytics.approved_submissions} 
          color="green"
        />
      </div>

      {/* Historical Charts */}
      {historical && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-brand" />
              Activity Trends
            </h3>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <select 
                value={period} 
                onChange={(e) => setPeriod(Number(e.target.value))}
                className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-brand/20"
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Submissions Chart */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Submissions</span>
                <span className="text-lg font-bold text-brand">{historical.totals?.submissions || 0}</span>
              </div>
              <SimpleLineChart 
                data={historical.time_series} 
                dataKey="submissions" 
                color="#2563eb"
                height={100}
              />
            </div>
            
            {/* Approvals Chart */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Approvals</span>
                <span className="text-lg font-bold text-green-600">{historical.totals?.approvals || 0}</span>
              </div>
              <SimpleLineChart 
                data={historical.time_series} 
                dataKey="approvals" 
                color="#16a34a"
                height={100}
              />
            </div>
            
            {/* Visitors Chart */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Visitors</span>
                <span className="text-lg font-bold text-purple-600">{historical.totals?.visitors || 0}</span>
              </div>
              <SimpleLineChart 
                data={historical.time_series} 
                dataKey="visitors" 
                color="#9333ea"
                height={100}
              />
            </div>
            
            {/* Reviews Chart */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Reviews</span>
                <span className="text-lg font-bold text-amber-600">{historical.totals?.reviews || 0}</span>
              </div>
              <SimpleLineChart 
                data={historical.time_series} 
                dataKey="reviews" 
                color="#d97706"
                height={100}
              />
            </div>
          </div>
        </div>
      )}

      {/* Revenue & Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Revenue */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <PoundSterling className="w-5 h-5 text-green-600" />
            Revenue Estimate
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Monthly</span>
              <span className="text-2xl font-bold text-green-600">
                £{analytics.revenue?.estimated_monthly?.toFixed(2) || '0.00'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Annual</span>
              <span className="text-2xl font-bold text-green-600">
                £{analytics.revenue?.estimated_annual?.toFixed(2) || '0.00'}
              </span>
            </div>
            <div className="pt-4 border-t border-gray-100 text-xs text-gray-400">
              Based on {analytics.total_premium} premium subscriptions at £{analytics.revenue?.premium_price}/year
            </div>
          </div>
        </div>

        {/* Venue Types */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-brand" />
            Venues by Type
          </h3>
          {venueTypeData.length > 0 ? (
            <SimpleBarChart data={venueTypeData} labelKey="type" valueKey="count" />
          ) : (
            <p className="text-gray-500 text-center py-4">No venue data</p>
          )}
        </div>
      </div>

      {/* Venues by City */}
      {analytics.venues_by_city?.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-brand" />
            Top Cities
          </h3>
          <SimpleBarChart 
            data={analytics.venues_by_city} 
            labelKey="city" 
            valueKey="count" 
          />
        </div>
      )}

      {/* Visitor Insights Section */}
      <VisitorInsights />
    </div>
  );
}

// Visitor Insights Component
function VisitorInsights() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  useEffect(() => {
    fetchInsights();
  }, [days]);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/visitor-analytics?days=${days}`);
      setInsights(response.data);
    } catch (error) {
      console.error("Failed to fetch visitor insights:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!insights) return null;

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const byDayData = daysOfWeek.map(day => ({
    day: day.slice(0, 3),
    count: insights.by_day?.[day] || 0
  })).filter(d => d.count > 0);

  const deviceData = Object.entries(insights.by_device || {}).map(([type, count]) => ({
    type: type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Unknown',
    count
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Eye className="w-5 h-5 text-purple-600" />
          Visitor Insights
        </h3>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-purple-50 rounded-xl p-4">
          <p className="text-xs text-purple-600 font-medium">Total Visits</p>
          <p className="text-2xl font-bold text-purple-700">{insights.summary?.total_visits || 0}</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4">
          <p className="text-xs text-blue-600 font-medium">Unique Sessions</p>
          <p className="text-2xl font-bold text-blue-700">{insights.summary?.unique_sessions || 0}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4">
          <p className="text-xs text-green-600 font-medium">Avg Time on Page</p>
          <p className="text-2xl font-bold text-green-700">{insights.summary?.avg_time_on_page || 0}s</p>
        </div>
        <div className="bg-orange-50 rounded-xl p-4">
          <p className="text-xs text-orange-600 font-medium">Avg Scroll Depth</p>
          <p className="text-2xl font-bold text-orange-700">{insights.summary?.avg_scroll_depth || 0}%</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* By Device */}
        {deviceData.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-4">Visits by Device</h4>
            <SimpleBarChart data={deviceData} labelKey="type" valueKey="count" />
          </div>
        )}

        {/* By Day */}
        {byDayData.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-4">Visits by Day</h4>
            <SimpleBarChart data={byDayData} labelKey="day" valueKey="count" />
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Top Pages */}
        {insights.top_pages?.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-4">Top Pages</h4>
            <div className="space-y-2">
              {insights.top_pages.slice(0, 5).map((page) => (
                <div key={page.page} className="flex justify-between text-sm">
                  <span className="text-gray-600 truncate max-w-[200px]">{page.page}</span>
                  <span className="font-medium text-gray-900">{page.views}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Locations */}
        {insights.top_locations?.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h4 className="text-sm font-semibold text-gray-700 mb-4">Visitor Locations</h4>
            <div className="space-y-2">
              {insights.top_locations.slice(0, 5).map((loc) => (
                <div key={loc.city} className="flex justify-between text-sm">
                  <span className="text-gray-600">{loc.city}</span>
                  <span className="font-medium text-gray-900">{loc.visits}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Top Referrers */}
      {insights.top_referrers?.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h4 className="text-sm font-semibold text-gray-700 mb-4">Top Referrers</h4>
          <div className="space-y-2">
            {insights.top_referrers.slice(0, 5).map((ref) => (
              <div key={ref.referrer || 'direct'} className="flex justify-between text-sm">
                <span className="text-gray-600 truncate max-w-[300px]">{ref.referrer || "Direct"}</span>
                <span className="font-medium text-gray-900">{ref.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Premium Owner Analytics
export function PremiumAnalytics({ venueId, ownerId }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (venueId && ownerId) {
      fetchAnalytics();
    }
  }, [venueId, ownerId]);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/${venueId}?owner_id=${ownerId}`);
      setAnalytics(response.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Premium subscription required to view analytics");
      } else {
        setError("Failed to load analytics");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-24 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 bg-amber-50 rounded-xl border border-amber-200">
        <Crown className="w-10 h-10 text-amber-500 mx-auto mb-2" />
        <p className="text-amber-700 font-medium">{error}</p>
        <p className="text-amber-600 text-sm mt-1">Upgrade to premium to unlock detailed analytics</p>
      </div>
    );
  }

  if (!analytics) {
    return <div className="text-center text-gray-500 py-8">No analytics available</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          icon={Eye} 
          label="Total Views" 
          value={analytics.views} 
          color="blue"
        />
        <StatCard 
          icon={Heart} 
          label="Favorites" 
          value={analytics.favorites_count} 
          color="red"
        />
        <StatCard 
          icon={Star} 
          label="Avg Rating" 
          value={analytics.avg_rating?.toFixed(1) || '0.0'} 
          subtext={`${analytics.review_count} reviews`}
          color="amber"
        />
        <StatCard 
          icon={TrendingUp} 
          label="Avg Rating" 
          value={analytics.avg_rating?.toFixed(1) || '0.0'} 
          subtext="out of 10"
          color="green"
        />
      </div>

      {/* Recent Reviews */}
      {analytics.reviews?.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Reviews</h3>
          <div className="space-y-4">
            {analytics.reviews.slice(0, 5).map((review) => (
              <div key={review.id || review._id || `review-${review.user_name}-${review.rating}`} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                <div className="flex items-center gap-1 text-amber-500">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="font-medium">{review.rating}</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-700">{review.comment || 'No comment'}</p>
                  <p className="text-xs text-gray-400 mt-1">{review.user_name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminAnalytics;
